import React, { useCallback } from 'react';
import styled from 'styled-components';
import Link, { createLinkObject } from '@core/PageSwitch/Link';
import { Colors, presetColors } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import Site from '@client/store/Site';
import { useAuthenticatedUser } from '@client/utils/AuthStore';
import { usePath } from './UsePath';

const Mainlinks = styled.ul`
	display: flex;
	flex-direction: column;
	justify-content: center;
	text-align: center;
	width: 100vw;
	padding: 0;
	padding-top: 20px;
	@media all and (min-height: 724px) {
		padding-top: 50px;
	}
	margin: 0;
	li {
		list-style-type: none;
	}
`;

const Mainlink = styled(Link)<{ $active: boolean }>`
	${TextStyle.size('p3')}
	color: ${props => (props.$active ? Colors.newsBackground : Colors.lightText)};
	&:hover {
		color: ${Colors.newsBackground};
	}

	@media all and (min-height: 724px) {
		${TextStyle.size('h6')}
	}
	@media all and (min-height: 1024px) {
		${TextStyle.size('h4')}
	}

	@media all and (max-width: 554px) {
		${TextStyle.size('p3')}
	}
`;

const ComingSoonLink = styled.div<{ $rotation?: string }>`
	${TextStyle.size('h6', 'h4', 'laptop')}

	&.unavailable {
		color: rgba(255, 255, 255, 0.1);
		&:before {
			content: 'kommer snart';
			position: absolute;
			background-color: ${presetColors.sunkiss};
			${TextStyle.size('xsmall', 'p5')}
			color: ${Colors.darkText};
			padding: 8px 18px 5px 18px;
			transform: translate(-50%, 50%) rotate(${props => props.$rotation});
			transform-origin: center;
			left: 50%;
		}
	}
`;

const Sublinks = styled.ul`
	padding: 0;
	padding-top: 30px;
	display: flex;
	width: 100vw;
	flex-direction: column;
	${TextStyle.family('title')}
	${TextStyle.size('large', 'p4', 'laptop')}
	justify-content: center;
	text-align: center;
	margin: 0;

	li {
		padding-bottom: 10px;
		list-style-type: none;
	}
`;

const Sublink = styled(Link)`
	padding-bottom: 10px;
	&:hover {
		color: ${Colors.newsBackground};
	}
`;

export default function MenuLinks() {
	const topMenuState = Site.use(state => state.topMenu);

	const path = usePath();
	const user = useAuthenticatedUser();

	const getNewRotation = useCallback((index: number) => {
		let rotation: number = Math.round(Math.random() * 5 + 5);
		if (index % 2) rotation = rotation * -1;
		return rotation + 'deg';
	}, []);

	return (
		<>
			<Mainlinks>
				{topMenuState.primaryLinks.map((link, index) =>
					!link.internalLink && !link.link ? (
						<ComingSoonLink key={'pl' + index} $rotation={getNewRotation(index)} className={'unavailable menu-item'}>
							{link.title}
						</ComingSoonLink>
					) : (
						<li key={'pl-div-' + index} className={'menu-item'}>
							<Mainlink data={link} key={'pl' + index} $active={link.internalLink?.slug === path}>
								{link.title}
							</Mainlink>
						</li>
					)
				)}

				{user && (
					<li key={'pl-div-min-side'} className={'menu-item'}>
						<Mainlink
							data={createLinkObject({ _prefix: '', slug: '/min-side' })}
							key={'min-side'}
							$active={'min-side' === path}
						>
							{'Mit Smuk'}
						</Mainlink>
					</li>
				)}
			</Mainlinks>
			<Sublinks className={'menu-item'}>
				{topMenuState.secondaryLinks.map((link, index) => (
					<li key={'sl-div-' + index}>
						<Sublink key={'sl' + index} data={link}>
							{link.title}
						</Sublink>
					</li>
				))}
			</Sublinks>
		</>
	);
}
